import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Joyride, { CallBackProps, STATUS, Step, EVENTS, ACTIONS } from 'react-joyride';
import welcome from "../../assets/images/Welcome.png";

const Tour = () => {
    const { user } = useSelector((state: any) => state.login);
    const [runTour, setRunTour] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);

    // Define the steps for the tour
    const steps: Step[] = [
        {
            target: 'body', // attaches to the body (full screen)
            content: (
                <div>
                    <img src={welcome} height={100} width={100}></img>
                    <p className= "joyride" style={{ fontSize: "14px", fontWeight: "bold", marginTop: "15px" }}>Welcome to Unified Data Studio Dashboard</p>
                    <p className= "joyride" style={{ fontSize: "14px" }}>
                        This walkthrough will guide you through the key features and sections of the platform, helping you understand and analyze your campaign performance with ease.
                    </p>
                    <p className= "joyride" style={{ fontSize: "14px" }}>Let’s take a quick tour to get familiar with everything.</p>
                </div>
            ),
            placement: 'center',
            disableBeacon: true,
            spotlightClicks: false,
            styles: {
                options: {
                    width: 620,
                    zIndex: 1300,
                },
            },
        },
        {
            target: '.analyticsjoy',
            content: (<p className= "joyride" style={{ fontSize: "14px" }}>Access detailed performance metrics for your campaigns. This is your go-to space for monitoring impressions, clicks, conversions, and spend.</p>),
            placement: 'bottom-start',
            disableBeacon: true,
        },
        {
            target: '.reportsjoy',
            content: (<p className= "joyride" style={{ fontSize: "14px" }}>View and download custom or scheduled reports. Perfect for sharing with clients or tracking progress over time.</p>),
            placement: 'bottom-start',
            disableBeacon: true,
        },
        ...((["admin", "AE", "AO"].includes(user.user.user_type) ||
            (user.user.permission_type === 'admin' && user.user.user_type === 'Client'))
            ? [{
                target: '.settingjoy',
                content: (
                    <p className= "joyride" style={{ fontSize: "14px" }}>
                        Customize your preferences, manage team users, and configure key aspects of your account in the Settings section.
                    </p>
                ),
                placement: 'bottom-start' as const,  // 👈 Fix here
                disableBeacon: true,
                spotlightPadding: 0,
            }]
            : []),
        {
            target: '.profilejoy',
            content: (<p className= "joyride" style={{ fontSize: "14px" }}>This is your profile section. You can manage your personal info and settings here.</p>),
            placement: 'bottom-start',
            disableBeacon: true,
        },
        {
            target: '.topnavjoy',
            content: (<p className= "joyride" style={{ fontSize: "14px" }}>Use these dropdowns and date range selectors to filter your analytics by campaign, status and time period. This helps narrow down the data to exactly what you need to review.</p>),
            placement: 'bottom-start',
            disableBeacon: true,
        },
        {
            target: '.chartsjoy',
            content: (<p className= "joyride" style={{ fontSize: "14px" }}>This dashboard gives you a visual overview of key campaign metrics like impressions, clicks, conversions, CTR, and average CPM. Use the interactive brush chart to filter and analyze performance trends over time.</p>),
            placement: 'center',
            disableBeacon: true,
        },
    ];

    const ThemeStyles = {
        options: {
            arrowColor: '#ffffff',
            backgroundColor: '#ffffff',
            primaryColor: '#6366f1',
            textColor: '#1f2937',
            width: 520,
            zIndex: 1300,
        },
        arrow: {
            borderRadius: '4px',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
            transform: 'rotate(45deg)',
            width: '12px',
            height: '12px',
            transition: 'all 0.3s ease-in-out', // ✅ Smooth arrow repositioning
        },
        buttonClose: {
            color: '#9ca3af',
            fontSize: '14px',
            position: 'absolute',
            top: '12px',
            right: '16px',
            ':hover': {
                color: '#374151',
            },
        },
        buttonNext: {
            backgroundColor: '#6366f1',
            color: '#ffffff',
            borderRadius: '8px',
            padding: '10px 20px',
            fontWeight: 600,
            fontSize: '14px',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
            transition: 'background-color 0.3s ease',
            ':hover': {
                backgroundColor: '#4f46e5',
            },
        },
        buttonBack: {
            color: '#6b7280',
            fontWeight: 500,
            fontSize: '14px',
            marginRight: '12px',
            transition: 'color 0.3s ease',
            ':hover': {
                color: '#374151',
            },
        },
        tooltipContainer: {
            padding: '24px 0px 1px',
            borderRadius: '16px',
            backgroundColor: '#ffffff',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.12)',
            transition: 'opacity 0.3s ease, transform 0.3s ease-in-out', // ✅ Smooth fade & motion
            opacity: 1,
            transform: 'translateY(0)', // You can animate this if needed
        },
        tooltipTitle: {
            color: '#111827',
            fontWeight: 700,
            fontSize: '14px',
            marginBottom: '0px',
        },
    };


    // Show tour on first render
    useEffect(() => {
        const lastLogin = user?.user?.is_first_time_login;
        const tourAlreadySeen = localStorage.getItem('tourSeen');

        if (lastLogin && !tourAlreadySeen) {
            setStepIndex(0);
            setRunTour(true);
        } else {
            setRunTour(false);
        }
    }, []);

    // Handle tour completion
    const handleTourCallback = (data: CallBackProps) => {
        const { status, index, type, action } = data;

        if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
            const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
            const nextStep = steps[nextIndex];

            // For the charts step, scroll it into view first so spotlight aligns correctly
            if (nextStep?.target === '.chartsjoy') {
                const el = document.querySelector('.chartsjoy');
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    setTimeout(() => setStepIndex(nextIndex), 400);
                    return;
                }
            }

            setStepIndex(nextIndex);
        }

        if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
            setRunTour(false);
            setStepIndex(0);
            localStorage.setItem('tourSeen', 'true'); // Save to prevent showing again
        }
    };

    return (
        <>
            {runTour && <Joyride
                steps={steps}
                run={runTour}
                stepIndex={stepIndex}
                continuous
                showSkipButton
                showProgress
                disableScrolling={true}
                spotlightPadding={10}
                styles={ThemeStyles as any}
                locale={{
                    back: 'Back',
                    close: 'Close',
                    last: 'Done',
                    next: 'Next',
                    skip: 'Skip',
                }}
                callback={handleTourCallback}
            />}
        </>
    );
}

export default Tour;
